import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";

import { useAuth } from "auth";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocale } from "lib/i18n/client";
import { localeToIntlCode } from "lib/i18n/config";
import { getTranslations, interpolateTemplate } from "lib/i18n/dictionary";

import { fetchMySubscription, type SubscriptionOverview } from "./subscription.api";
import { PLAN_COMPARE_ROWS, subscriptionStatus } from "./subscription-status";

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL?.trim() || "";
const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL?.trim() || "";

// "Моя підписка" in admin: the Account's Plan, Free and Pro side by side, and where Pro is bought
// (the Matcha app; stores cannot sell on the web) or managed and cancelled (the store it came from).
export function SubscriptionPage() {
  const locale = useLocale();
  const t = getTranslations(locale).subscriptionPage;
  const { accessToken } = useAuth();
  const [overview, setOverview] = useState<SubscriptionOverview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMySubscription(accessToken ?? undefined)
      .then((result) => alive && setOverview(result))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [accessToken]);

  const isPro = overview ? overview.plan.key !== "free" : false;
  const status = overview ? subscriptionStatus(overview.subscription) : null;
  const date = status?.date.toLocaleDateString(localeToIntlCode[locale], {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mt={4} mb={3} display="flex" flexDirection="column" gap={3} maxWidth={720}>
        {!overview ? (
          <Card>
            <SoftBox py={6} display="flex" justifyContent="center" data-testid="subscription-loading">
              {failed ? (
                <SoftTypography variant="button" color="text">
                  {t.loadFailed}
                </SoftTypography>
              ) : (
                <CircularProgress color="info" aria-label={t.loading} />
              )}
            </SoftBox>
          </Card>
        ) : (
          <>
            <Card data-testid="subscription-plan">
              <SoftBox p={3}>
                <SoftTypography variant="caption" color="text">
                  {t.current}
                </SoftTypography>
                <SoftTypography variant="h4" fontWeight="bold">
                  {isPro ? t.names.pro : t.names.free}
                </SoftTypography>
                {status ? (
                  <SoftTypography variant="button" color="text" fontWeight="regular" data-testid="subscription-status">
                    {interpolateTemplate(t.status[status.kind], { date: date ?? "" })}
                  </SoftTypography>
                ) : null}
                {isPro && overview.subscription?.provider ? (
                  <SoftBox mt={1}>
                    <SoftTypography variant="caption" color="text" data-testid="subscription-manage">
                      {overview.subscription.provider === "STRIPE"
                        ? t.manage.INTERNAL
                        : t.manage[overview.subscription.provider]}
                    </SoftTypography>
                  </SoftBox>
                ) : null}
              </SoftBox>
            </Card>
            <Card data-testid="subscription-compare">
              <SoftBox p={3}>
                <SoftBox display="flex" pb={1}>
                  <SoftBox flex={1} />
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ width: 96, textAlign: "center" }}>
                    {t.names.free}
                  </SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" color="success" sx={{ width: 96, textAlign: "center" }}>
                    {t.names.pro}
                  </SoftTypography>
                </SoftBox>
                {PLAN_COMPARE_ROWS.map((row) => (
                  <SoftBox
                    key={row}
                    display="flex"
                    alignItems="center"
                    py={1}
                    sx={{ borderTop: "1px solid", borderColor: "grey.200" }}
                    data-testid={`subscription-compare-${row}`}
                  >
                    <SoftTypography variant="button" fontWeight="regular" sx={{ flex: 1 }}>
                      {t.compare[row].label}
                    </SoftTypography>
                    <CompareValue value={t.compare[row].free} />
                    <CompareValue value={t.compare[row].pro} pro />
                  </SoftBox>
                ))}
              </SoftBox>
            </Card>
            {!isPro ? (
              <Card data-testid="subscription-get-pro">
                <SoftBox p={3} display="flex" flexDirection="column" gap={1.5}>
                  <SoftTypography variant="h6">{t.getProTitle}</SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    {t.getProBody}
                  </SoftTypography>
                  <SoftBox display="flex" gap={1.5} flexWrap="wrap">
                    {APP_STORE_URL ? (
                      <SoftButton component="a" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" color="dark" data-testid="subscription-app-store">
                        {t.appStore}
                      </SoftButton>
                    ) : null}
                    {GOOGLE_PLAY_URL ? (
                      <SoftButton component="a" href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" color="dark" data-testid="subscription-google-play">
                        {t.googlePlay}
                      </SoftButton>
                    ) : null}
                  </SoftBox>
                </SoftBox>
              </Card>
            ) : null}
          </>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

function CompareValue({ value, pro = false }: { value: string; pro?: boolean }) {
  return (
    <SoftBox sx={{ width: 96, display: "flex", justifyContent: "center" }}>
      {value === "✓" ? (
        <CheckIcon fontSize="small" color={pro ? "success" : "inherit"} />
      ) : (
        <SoftTypography variant="button" fontWeight={pro ? "bold" : "regular"} color={pro ? "success" : "text"}>
          {value}
        </SoftTypography>
      )}
    </SoftBox>
  );
}
